import {
  createBusinessEntityLocation,
  TUpdateLocationParams,
  TCreateBusinessEntityLocation,
  updateBusinessEntityLocation,
} from '@commenda/api/locations-2';
import { useContentBasedStateOptions } from '@commenda/api/content-library';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Icons,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { Country } from '@commenda/prisma';
import { addressSchema } from '@commenda/schema/address';
import { ILocation, LocationTypeRevamp } from '@commenda/types';
import {
  LOCATION_TYPE_TO_TEXT,
  NonTimezoneSensitiveDate,
  throwApiError,
} from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import AddressGroupRHF from '../../../../../components/AddressGroupRHF';
import { useNotification } from '../../../../../hooks/useNotification';
import {
  createDateSchema,
  createPastOrPresentDateSchema,
} from '../../Registration/Modals/addRegistrationSchema';

const createAddLocationSchema = z
  .object({
    locationName: z.string().min(1, 'Location name is required'),
    startDate: createPastOrPresentDateSchema(true),
    endDate: createDateSchema(false),
    employeeCount: z.coerce.number().min(1).optional(),
    locationTypes: z.array(z.string()).optional(),
    locationType: z
      .string()
      .min(1, 'Location type is required')
      .refine(
        (value) =>
          Object.values(LocationTypeRevamp).includes(
            value as LocationTypeRevamp
          ),
        { message: 'Invalid location type' }
      ),
    address: addressSchema,
  })
  .refine(
    (data) => {
      if (
        !data.address ||
        !data.address.addressLine1 ||
        !data.address.city ||
        !data.address.country
      ) {
        return false;
      }

      return true;
    },
    {
      message: 'Address is required',
      path: ['address'],
    }
  )
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      return new Date(data.endDate) > new Date(data.startDate);
    },
    {
      message: 'End date must be after start date',
      path: ['endDate'],
    }
  );

type AddLocationModalFormFields = z.infer<typeof createAddLocationSchema>;

interface AddLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId: number;
  onSuccess: () => void;
  selectedLocation?: ILocation;
}

export const AddLocationModal = ({
  isOpen,
  onClose,
  businessEntityId,
  onSuccess,
  selectedLocation,
}: AddLocationModalProps) => {
  const isSaving = useToggle();
  const { setNotification } = useNotification();
  const [originalFormValues, setOriginalFormValues] =
    useState<AddLocationModalFormFields | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const methods = useForm<AddLocationModalFormFields>({
    resolver: zodResolver(createAddLocationSchema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    clearErrors,
    formState: { errors },
    setValue,
    watch,
    reset,
    register,
  } = methods;

  const watchedLocationType = watch('locationType');

  // Backend-driven state options for the location address: the picker only offers
  // subdivision codes the sales-tax validator accepts. While the request is in
  // flight, or when a country has no curated list, the empty list hides the
  // dropdown (rather than falling back to the country-state-city library).
  const watchedAddressCountry = watch('address.country');
  const contentStateOptions = useContentBasedStateOptions(
    businessEntityId,
    watchedAddressCountry
  );
  const addressStateOptions = useMemo(
    () =>
      watchedAddressCountry
        ? contentStateOptions.map((option) => ({
          code: option.value,
          displayedName: option.label,
        }))
        : undefined,
    [contentStateOptions, watchedAddressCountry]
  );

  const isAddressInputDisabled = Boolean(
    selectedLocation?.locationTypes?.some((type) =>
      [
        LocationTypeRevamp.REGISTERED_OFFICE,
        LocationTypeRevamp.MAILING_ADDRESS,
      ].includes(type)
    )
  );

  useEffect(() => {
    if (!selectedLocation || !originalFormValues) {
      setHasChanges(false);
      return;
    }

    const subscription = watch((value) => {
      const currentValues = value as AddLocationModalFormFields;
      const changed =
        currentValues.locationName !== originalFormValues.locationName ||
        ((originalFormValues.startDate || currentValues.startDate) &&
          currentValues.startDate !== originalFormValues.startDate) ||
        ((originalFormValues.endDate || currentValues.endDate) &&
          currentValues.endDate !== originalFormValues.endDate) ||
        (currentValues.employeeCount &&
          currentValues.employeeCount !== originalFormValues.employeeCount) ||
        currentValues.locationType !== originalFormValues.locationType ||
        JSON.stringify(currentValues.address) !==
        JSON.stringify(originalFormValues.address);

      setHasChanges(changed);
    });

    return () => subscription.unsubscribe();
  }, [watch, selectedLocation, originalFormValues, isOpen]);

  useEffect(() => {
    if (watch('locationType') !== LocationTypeRevamp.REMOTE_EMPLOYEE) {
      setValue('employeeCount', undefined);
    } else {
      setValue('employeeCount', originalFormValues?.employeeCount || 0);
    }
  }, [watch('locationType')]);

  useEffect(() => {
    if (selectedLocation) {
      setOriginalFormValues(null);
      const formData = {
        country: selectedLocation.address.country,
        locationName: selectedLocation.name,
        employeeCount: selectedLocation.locationTypes.some(
          (type) => type === LocationTypeRevamp.REMOTE_EMPLOYEE
        )
          ? selectedLocation.employeeCount || 0
          : undefined,
        startDate: selectedLocation.startDate
          ? new Date(selectedLocation.startDate).toISOString().split('T')[0]
          : undefined,
        endDate: selectedLocation.endDate
          ? new Date(selectedLocation.endDate).toISOString().split('T')[0]
          : undefined,
        locationType: selectedLocation.locationTypes?.[0] || '',
        // @ts-ignore
        locationTypes: selectedLocation.locationTypesArray,
        address: selectedLocation.address
          ? {
            addressLine1: selectedLocation.address.addressLine1,
            addressLine2: selectedLocation.address.addressLine2,
            addressLine3: selectedLocation.address.addressLine3 || '',
            city: selectedLocation.address.city,
            state: selectedLocation.address.state || '',
            postalCode: selectedLocation.address.postalCode,
            country: selectedLocation.address.country,
          }
          : undefined,
      };
      reset(formData);
      setOriginalFormValues(formData);
      setHasChanges(false);
    } else {
      reset({
        locationType: '',
        locationTypes: [],
        address: undefined,
        startDate: NonTimezoneSensitiveDate.now().toISO8601String(),
      });
      setOriginalFormValues(null);
      setHasChanges(false);
    }
  }, [selectedLocation, reset, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setOriginalFormValues(null);
      clearErrors();
      setHasChanges(false);
    }
  }, [isOpen]);

  const handleEmployeeCountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setValue(
      'employeeCount',
      value === '' || value === '0' ? undefined : parseInt(value, 10)
    );
  };

  const buildRequestBody = (
    data: AddLocationModalFormFields,
    isArray = false
  ): TCreateBusinessEntityLocation => ({
    locationName: data.locationName,
    employeeCount: data.employeeCount,
    startDate: data.startDate,
    endDate: data.endDate === '' ? null : data.endDate,
    locationTypes: isArray
      ? (data.locationTypes as LocationTypeRevamp[])
      : [data.locationType as LocationTypeRevamp],
    address: {
      addressLine1: data.address.addressLine1,
      addressLine2: data.address.addressLine2,
      addressLine3: data.address.addressLine3,
      city: data.address.city,
      state: data.address.state,
      postalCode: data.address.postalCode,
      country: data.address.country as Country,
    },
  });

  const onSave = async (data: AddLocationModalFormFields) => {
    if (isSaving.value) return;

    isSaving.on();

    try {
      const hasMultipleLocationTypes = data.locationTypes?.length > 1;
      const isLocationTypeInArray = data.locationTypes?.includes(
        data.locationType
      );

      const shouldSplitLocationTypes =
        hasMultipleLocationTypes && isLocationTypeInArray;

      if (shouldSplitLocationTypes) {
        // this is to handle the where user had added multiple location types before in old ui
        // we need to create a new location for the location type and update the existing location by removing the location type from the array
        await handleLocationTypeSplit(data);
      } else if (isLocationTypeInArray && !hasMultipleLocationTypes) {
        await handleStandardSave(data);
      } else {
        await createNewLocation(data);
      }

      setNotification({
        message: selectedLocation
          ? 'Location updated successfully'
          : 'Location added successfully',
        show: true,
        valid: true,
        title: 'Success',
      });

      mixpanel.track('ENTITY_BUSINESS_PRESENCE_ADDED', {
        source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
        surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
        is_edit: Boolean(selectedLocation),
      });

      reset();
      onSuccess();
      onClose();
    } catch (error) {
      setNotification({
        title: 'Error',
        message:
          error instanceof Error && error.message
            ? error.message
            : selectedLocation
              ? 'Failed to edit location.'
              : 'Failed to add location.',
        show: true,
        valid: false,
      });
    } finally {
      isSaving.off();
    }
  };

  const handleLocationTypeSplit = async (data: AddLocationModalFormFields) => {
    const remainingLocationTypes = data.locationTypes.filter(
      (type) => type !== data.locationType
    );

    const newLocationBody = buildRequestBody({
      ...data,
      locationTypes: [data.locationType],
    });

    const newLocation = await createBusinessEntityLocation(
      newLocationBody as TCreateBusinessEntityLocation,
      businessEntityId
    );

    if (!newLocation.ok) {
      // Surface the backend's real error and skip the success/close path.
      await throwApiError(newLocation, 'Failed to create new location');
    }

    const updateBody = buildRequestBody(
      {
        ...selectedLocation,
        startDate: selectedLocation.startDate
          ? NonTimezoneSensitiveDate.fromNativeDate(
            new Date(selectedLocation.startDate)
          ).toISO8601String()
          : undefined,
        endDate: selectedLocation.endDate
          ? NonTimezoneSensitiveDate.fromNativeDate(
            new Date(selectedLocation.endDate)
          ).toISO8601String()
          : undefined,
        locationTypes: remainingLocationTypes,
      },
      true
    );

    const updatedLocation = await updateBusinessEntityLocation(
      businessEntityId,
      selectedLocation.id,
      updateBody
    );

    if (!updatedLocation.ok) {
      await throwApiError(
        updatedLocation,
        'Failed to update existing location'
      );
    }
  };

  const handleStandardSave = async (data: AddLocationModalFormFields) => {
    const body = buildRequestBody(data);

    const response = selectedLocation
      ? await updateBusinessEntityLocation(
        businessEntityId,
        selectedLocation.id,
        body
      )
      : await createBusinessEntityLocation(body as TCreateBusinessEntityLocation, businessEntityId);

    if (!response.ok) {
      await throwApiError(response, 'Failed to save location');
    }
  };

  const createNewLocation = async (data: AddLocationModalFormFields) => {
    const body = buildRequestBody({
      ...data,
      locationTypes: [data.locationType],
    });

    const response = await createBusinessEntityLocation(body as TCreateBusinessEntityLocation, businessEntityId);

    if (!response.ok) {
      await throwApiError(response, 'Failed to save location');
    }
  };
  const modalTitle = selectedLocation
    ? 'Edit Business Presence'
    : 'Add Business Presence';
  const modalDescription = selectedLocation
    ? 'Update the details for this business presence'
    : 'Enter the details for this business presence';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        preventCloseOnOutsideClick
        className="min-w-[650px] overflow-y-hidden"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSave)}>
            <DialogHeader className="flex flex-col gap-xs items-start">
              <DialogTitle>{modalTitle}</DialogTitle>
              <DialogDescription>{modalDescription}</DialogDescription>
            </DialogHeader>

            <div className="p-3xl max-h-[440px] overflow-y-auto flex flex-col gap-2xl">
              <Alert
                variant="info"
                title="Single entity tracking"
                description="Only add presence information for one legal entity. If you have subsidiaries, they should be tracked separately with their own presence data."
              />

              <div className="flex flex-col gap-2xl">
                {/* Presence Type Field */}
                <FieldWrapper>
                  <div className="flex items-center gap-xs">
                    <Label htmlFor="presenceType">Presence type</Label>
                    <Tooltip
                      side="right"
                      content="Select the type of location you are adding"
                    >
                      <Icons.InfoCircle className="h-4 w-4 text-fg-tertiary" />
                    </Tooltip>
                  </div>
                  <Select
                    onValueChange={(value) => {
                      clearErrors('locationType');
                      setValue('locationType', value);
                    }}
                    value={watch('locationType')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(LocationTypeRevamp)
                        .filter(
                          (type) =>
                            type !== LocationTypeRevamp.REGISTERED_OFFICE &&
                            type !== LocationTypeRevamp.MAILING_ADDRESS
                        )
                        .map((type) => (
                          <SelectItem key={type} value={type.toString()}>
                            {LOCATION_TYPE_TO_TEXT[type] ?? type}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {errors?.locationType?.message && (
                    <p className="text-red-500">
                      {errors?.locationType?.message}
                    </p>
                  )}
                </FieldWrapper>

                {/* Location Name Field */}
                <FieldWrapper>
                  <div className="flex items-center gap-xs">
                    <Label htmlFor="locationName">Location name</Label>
                    <Tooltip
                      side="right"
                      content="Provide a name to this presence to easily identify it."
                    >
                      <Icons.InfoCircle className="h-4 w-4 text-fg-tertiary" />
                    </Tooltip>
                  </div>
                  <Input
                    id="locationName"
                    placeholder="Enter location name"
                    error={errors?.locationName?.message}
                    maxLength={2000}
                    minLength={1}
                    disabled={isSaving.value}
                    {...register('locationName')}
                  />
                </FieldWrapper>

                {/* Start Date Field */}
                <FieldWrapper>
                  <div className="flex items-center gap-xs">
                    <Label htmlFor="startDate">Start date</Label>
                    <Tooltip
                      side="right"
                      content="The date this location was opened."
                    >
                      <Icons.InfoCircle className="h-4 w-4 text-fg-tertiary" />
                    </Tooltip>
                  </div>
                  <Input
                    {...register('startDate')}
                    type="date"
                    placeholder="Select date"
                    disabled={isSaving.value}
                    error={errors?.startDate?.message}
                    maxLength={10}
                  />
                </FieldWrapper>

                {/* End Date Field */}
                <FieldWrapper>
                  <div className="flex items-center gap-xs">
                    <Label htmlFor="endDate">End date (optional)</Label>
                    <Tooltip
                      side="right"
                      content="The date this location was closed (leave blank if still active)."
                    >
                      <Icons.InfoCircle className="h-4 w-4 text-fg-tertiary" />
                    </Tooltip>
                  </div>
                  <Input
                    {...register('endDate')}
                    type="date"
                    placeholder="Select date"
                    disabled={isSaving.value}
                    error={errors?.endDate?.message}
                    maxLength={10}
                  />
                </FieldWrapper>

                {/* Employee Count Field */}
                {watchedLocationType === LocationTypeRevamp.REMOTE_EMPLOYEE && (
                  <FieldWrapper>
                    <Label htmlFor="employeeCount">Employee count</Label>
                    <Input
                      id="employeeCount"
                      placeholder="Enter employee count"
                      error={errors?.employeeCount?.message}
                      disabled={isSaving.value}
                      {...register('employeeCount')}
                      onChange={handleEmployeeCountChange}
                      type="number"
                      min={0}
                      max={100000000}
                      defaultValue={0}
                    />
                  </FieldWrapper>
                )}

                {/* Address Fields */}
                <AddressGroupRHF
                  pathPrefix="address"
                  disabled={isAddressInputDisabled}
                  stateOptions={addressStateOptions}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={
                  isSaving.value ||
                  (selectedLocation && !hasChanges) ||
                  !watch('locationType')
                }
              >
                {isSaving.value && <LoadingIndicator size={16} />}
                <span>{selectedLocation ? 'Save changes' : 'Save'}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
